// QuestionHandler.js
import {queryDataAttr} from "./advanced-player.";
import Hammer from "hammerjs";

const CLASS_PREFIX_DEFAULT = "advanced-player";
const DATA_PREFIX_DEFAULT = "data-advanced-player";

/**
 * Вспомогательный класс для рендеринга вопросов разных типов,
 * обработки выбора вариантов, свайпов, открытого ввода и т. д.
 *
 * Использование:
 *   QuestionHandler.render(questionObject, showIndex, {
 *     onAnswer: async (questionUid, answerData) => { … },
 *     onContinue: () => { … },
 *     onRetry: () => { … }
 *   }, {
 *     container: this.$questionOverlay,
 *     templates: {
 *       select: this.$questionSelectTemplate,
 *       swipe: this.$questionSwipeTemplate,
 *       open_text: this.$questionOpenTemplate
 *     },
 *     classPrefix: this.classPrefix,
 *     dataPrefix: this.dataPrefix,
 *     total: totalQuestionsCount
 *   });
 */

export default class AdvancedQuestionHandler {
    /**
     * Основной метод для рендеринга любого вопроса.
     *
     * @param {Object} question  — объект вопроса с полями: type, text, answers, left_answer/right_answer и т. д.
     * @param {Number} showIndex — порядковый номер вопроса («ВОПРОС 2 ИЗ 5»).
     * @param {Object} callbacks — набор колбэков: onAnswer(questionUid, answerData), onContinue(), onRetry().
     * @param {Object} options   — конфигурация:
     *   {
     *     container: DOM-элемент, куда вставляем,
     *     templates: { select, swipe, open_text },
     *     classPrefix: строка (префикс классов),
     *     dataPrefix: строка (префикс дата-атрибутов),
     *     total: общее число вопросов (Number)
     *     exerciseId: идентификатор упражнения (Number или строка)
     *     videoId
     *   }
     */
    static render(question, showIndex, callbacks, options) {
        const {onAnswer, onContinue, onRetry} = callbacks;
        const {
            container,
            templates,
            classPrefix = CLASS_PREFIX_DEFAULT,
            dataPrefix = DATA_PREFIX_DEFAULT,
            total = "",
            exerciseId,
            videoId,
            type
        } = options;

        // Очищаем контейнер перед вставкой нового содержимого
        container.innerHTML = "";

        // Выбор метода рендеринга в зависимости от типа вопроса
        switch (question.type) {
            case "select":
                this._renderSelect(
                    question,
                    showIndex,
                    onAnswer,
                    onContinue,
                    onRetry,
                    {
                        container,
                        template: templates.select,
                        classPrefix,
                        dataPrefix,
                        total,
                        exerciseId,
                        videoId
                    }
                );
                break;

            case "swipe":
                this._renderSwipe(
                    question,
                    showIndex,
                    onAnswer,
                    onContinue,
                    onRetry,
                    {
                        container,
                        template: templates.swipe,
                        classPrefix,
                        dataPrefix,
                        total,
                        exerciseId,
                        videoId,
                        type
                    }
                );
                break;

            case "open_text":
                this._renderOpen(
                    question,
                    showIndex,
                    onAnswer,
                    onContinue,
                    onRetry,
                    {
                        container,
                        template: templates.open_text,
                        classPrefix,
                        dataPrefix,
                        total,
                        exerciseId,
                        videoId
                    }
                );
                break;

            default:
                console.warn(
                    `QuestionHandler: неизвестный тип вопроса "${question.type}"`
                );
        }
    }

    // === Общие утилиты для рендеринга ===

    /**
     * Если это последний вопрос (showIndex === total), меняет текст кнопки «Продолжить»
     * на «Посмотреть результаты» и перенаправляет на /exercise/{exerciseId}/result.
     *
     * @param {DocumentFragment} $tpl — клонированный шаблон.
     * @param {Number} showIndex    — порядковый номер вопроса.
     * @param {Number|String} total — общее число вопросов.
     * @param {Number|String} exerciseId — идентификатор упражнения.
     */
    static _handleLastQuestion($tpl, showIndex, total, exerciseId, videoId) {
        console.log(showIndex)
        console.log(total)
        if (showIndex !== total) {
            return;
        }

        const $continueBtn = queryDataAttr("continue-btn", $tpl);
        if (!$continueBtn) {
            return;
        }

        // Заменим старую кнопку на клон, чтобы снять все ненужные слушатели
        const $newBtn = $continueBtn.cloneNode(true);
        $newBtn.textContent = "Посмотреть результаты";
        $continueBtn.parentNode.replaceChild($newBtn, $continueBtn);

        $newBtn.addEventListener("click", () => {
            window.location.href = `/exercise/${videoId}/task/${exerciseId}/result`;
        });

        return $newBtn;
    }

    /**
     * Заполняет общие поля вопроса: метка «ВОПРОС X ИЗ Y», заголовок, текст разбора и скрывает описание,
     * если это не первый вопрос.
     *
     * @param {Object} question  — объект вопроса.
     * @param {Number} showIndex — порядковый номер вопроса.
     * @param {Object} opts      — { total, classPrefix, dataPrefix }.
     * @param {DocumentFragment} $tpl — клонированный шаблон.
     */
    static _fillCommonFields(question, showIndex, opts, $tpl) {
        const {total, classPrefix, dataPrefix} = opts;

        const $questionLabel = queryDataAttr("question-total", $tpl);
        const $questionTitle = queryDataAttr("question-title", $tpl);
        const $questionDesc = queryDataAttr("question-desc", $tpl);
        const $answerText = queryDataAttr("response-text", $tpl);

        // Метка «ВОПРОС X ИЗ Y»
        $questionLabel.innerText = `ВОПРОС ${showIndex} ИЗ ${total}`;

        // Если это не первый вопрос, скрываем блок описания
        if (showIndex !== 1 && $questionDesc) {
            $questionDesc.style.display = "none";
        }

        // Заголовок вопроса
        $questionTitle.textContent = question.text;
        // Текст разбора (скрыт по умолчанию в шаблоне)
        $answerText.textContent = question.response_text || "";
    }

    /**
     * Привязывает обработчики кнопок «Продолжить» и «Повторить» к шаблону.
     *
     * @param {DocumentFragment} $tpl       — клонированный шаблон.
     * @param {Function} onContinue  — колбэк при нажатии «Продолжить».
     * @param {Function} onRetry     — колбэк при нажатии «Повторить».
     */
    static _attachContinueRetry($tpl, onContinue, onRetry) {
        const $continueBtn = queryDataAttr("continue-btn", $tpl);
        const $retryBtn = queryDataAttr("retry-btn", $tpl);

        if ($continueBtn) {
            $continueBtn.addEventListener("click", () => {
                onContinue();
            });
        }

        if ($retryBtn) {
            $retryBtn.addEventListener("click", () => {
                onRetry();
            });
        }
    }

    // === Рендеринг select-вопросов ===

    /**
     * Рендерит вопрос типа «select» (множественный выбор или одиночный выбор).
     *
     * @param {Object} question  — объект вопроса с полем answers: [{ text, is_correct }, ...].
     * @param {Number} showIndex — порядковый номер вопроса.
     * @param {Function} onAnswer  — колбэк (questionUid, selectedAnswersArray).
     * @param {Function} onContinue — колбэк «Продолжить».
     * @param {Function} onRetry    — колбэк «Повторить».
     * @param {Object} opts — { container, template, classPrefix, dataPrefix, total }.
     */
    static _renderSelect(
        question,
        showIndex,
        onAnswer,
        onContinue,
        onRetry,
        opts
    ) {
        const {container, template, classPrefix, dataPrefix, total, exerciseId, videoId } = opts;
        const $tpl = document.importNode(template.content, true);

        // Заполняем общие поля (номер, текст вопроса и т.п.)
        this._fillCommonFields(question, showIndex, {total, classPrefix, dataPrefix}, $tpl);

        // Контейнер для вариантов и элементы управления
        const $answersContainer = queryDataAttr("question-answers", $tpl);
        const $answerText = queryDataAttr("response-text", $tpl);
        let $continueBtn = queryDataAttr("continue-btn", $tpl);
        const $retryBtn = queryDataAttr("retry-btn", $tpl);
        const $doneBtn = queryDataAttr("select-done-btn", $tpl);
        $doneBtn.style.display = "none";

        // Количество обязательных выборов (по умолчанию 1)
        const requiredCount = Number(question.selects) || 1;
        const selectedAnswers = new Set(); // храним UID выбранных ответов

        // Флаг уже завершён ли выбор, чтобы не обрабатывать клики после финализации
        let finalized = false;

        // Вспомогательная функция: выбран ли вариант
        const isAnswerInSelected = (answer) => selectedAnswers.has(answer._uid);

        // Функция финализации: отключает клики, показывает результат и вызывает onAnswer
        const tryFinalize = async () => {
            console.log('tryFinalize')
            if (finalized) return;

            console.log('next')
            // Проверяем минимальное число выбранных
            if (selectedAnswers.size < 1) {
                // Здесь можно показать сообщение или подсветку, что надо выбрать ещё варианты.
                // Например:
                // alert(`Пожалуйста, выберите как минимум ${requiredCount} вариантов.`);
                return;
            }
            finalized = true;

            // Скрываем кнопку done после клика
            $doneBtn.style.display = "none";

            // Блокируем дальнейшие клики на опции
            const allOptionButtons = Array.from(
                $answersContainer.querySelectorAll(`.${classPrefix}__answers-option`)
            );
            allOptionButtons.forEach((bt) => {
                bt.disabled = true;
            });
            // Проставляем финальные классы: correct/select/fade
            allOptionButtons.forEach((bt) => {
                const isBtnCorrect = bt.getAttribute(`${dataPrefix}-correct`) === "true";
                const uid = bt.getAttribute(`${dataPrefix}-uid`);
                const wasChosen = selectedAnswers.has(uid);

                // Убираем interim-класс
                bt.classList.remove(`${classPrefix}__answers-option--interim-selected`);

                if (wasChosen) {
                    // выбрали этот вариант
                    if (isBtnCorrect) {
                        bt.classList.add(`${classPrefix}__answers-option--correct`);
                    } else {
                        bt.classList.add(`${classPrefix}__answers-option--select`);
                    }
                } else {
                    // не выбирали этот вариант
                    if (isBtnCorrect) {
                        // правильный, но не выбрали
                        bt.classList.add(`${classPrefix}__answers-option--correct`);
                    } else {
                        bt.classList.add(`${classPrefix}__answers-option--fade`);
                    }
                }
            });

            // Показываем текст разбора и кнопку «Продолжить», скрываем «Повторить»
            $answerText.style.display = "flex";
            $continueBtn.style.display = "flex";
            $retryBtn.style.display = "none";

            // Собираем выбранные объекты ответов для передачи
            const chosenArray = question.answers.filter(ans => selectedAnswers.has(ans._uid));
            // Вызываем колбэк onAnswer
            await onAnswer(question._uid, chosenArray);
        };

        // Создаем кнопки для каждого варианта
        (question.answers || []).forEach((answer) => {
            const $btn = document.createElement("button");
            $btn.textContent = answer.text;
            $btn.classList.add(
                `${classPrefix}__select-option`,
                `${classPrefix}__answers-option`
            );
            // Сохраняем UID и correct как data-атрибуты
            $btn.setAttribute(`${dataPrefix}-uid`, answer._uid);
            $btn.setAttribute(`${dataPrefix}-correct`, answer.is_correct);




            $btn.addEventListener("click", () => {
                if (finalized) return; // уже показали результат
                const uid = answer._uid;
                // Если уже выбран, снимаем выбор
                if (isAnswerInSelected(answer)) {
                    selectedAnswers.delete(uid);
                    $btn.classList.remove(`${classPrefix}__answers-option--interim-selected`);
                    // Если после снятия выборов стало 0 и requiredCount>1, можно скрыть doneBtn до следующего выбора
                    if (requiredCount > 1 && selectedAnswers.size === 0) {
                        $doneBtn.style.display = "none";
                    }
                    return;
                }
                // Новый выбор
                // Логика для requiredCount===1: сразу финализируем
                if (requiredCount === 1) {
                    selectedAnswers.add(uid);
                    $btn.classList.add(`${classPrefix}__answers-option--interim-selected`);
                    // финализируем сразу
                    tryFinalize();
                    return;
                }
                // Для requiredCount>1: показываем кнопку doneBtn при первом выборе
                selectedAnswers.add(uid);
                $btn.classList.add(`${classPrefix}__answers-option--interim-selected`);
                if (selectedAnswers.size === 1) {
                    // первый выбор — показываем кнопку
                    $doneBtn.style.display = "flex";
                }
                // Не финализируем автоматически даже если size>=requiredCount
            });


            const $btnIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            const $use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
            $use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#complete');
            $btnIcon.appendChild($use);
            $btn.appendChild($btnIcon);

            $answersContainer.appendChild($btn);
        });

        // Обработчик кнопки «Done» (финализация при requiredCount>1)
        $doneBtn.addEventListener("click", async () => {
            console.log('click')
            await tryFinalize();
        });

        // Обработчики кнопок «Продолжить» и «Повторить»
        this._attachContinueRetry($tpl, onContinue, onRetry);

        container.appendChild($tpl);
    }


    // === Рендеринг swipe-вопросов ===

    /**
     * Рендерит вопрос типа «swipe» (свайп влево/вправо).
     *
     * @param {Object} question  — объект вопроса с полями: left_answer, right_answer, left_is_correct, right_is_correct.
     * @param {Number} showIndex — порядковый номер вопроса.
     * @param {Function} onAnswer  — колбэк (questionUid, { text }).
     * @param {Function} onContinue — колбэк «Продолжить».
     * @param {Function} onRetry    — колбэк «Повторить».
     * @param {Object} opts — { container, template, classPrefix, dataPrefix, total }.
     */
    static _renderSwipe(
        question,
        showIndex,
        onAnswer,
        onContinue,
        onRetry,
        opts
    ) {
        const {container, template, classPrefix, dataPrefix, total, exerciseId, videoId, type} = opts;
        const $tpl = document.importNode(template.content, true);

        // Заполняем общие поля
        this._fillCommonFields(question, showIndex, {total, classPrefix, dataPrefix}, $tpl);

        // Элементы из шаблона
        const $answerText = queryDataAttr("response-text", $tpl);
        let $continueBtn = queryDataAttr("continue-btn", $tpl);
        const $retryBtn = queryDataAttr("retry-btn", $tpl);
        const $swipeContainer = queryDataAttr("question-swipe", $tpl);
        const $swipeCard = queryDataAttr("question-swipe-card", $tpl);
        const $swipeCardText = queryDataAttr("question-swipe-card-text", $tpl);
        const $swipeCardIcon = queryDataAttr("question-swipe-card-icon", $tpl);
        const $tipLeft = queryDataAttr("question-swipe-left-tip", $tpl);
        const $tipRight = queryDataAttr("question-swipe-right-tip", $tpl);
        const $answerLeft = queryDataAttr("question-swipe-left", $tpl);
        const $answerRight = queryDataAttr("question-swipe-right", $tpl);


        if ($swipeCardIcon) {
            const $use = $swipeCardIcon.querySelector('use');
            if ($use) {
                let iconId;
                switch (type) {
                    case 'swipe_two':
                        iconId = '#swipe-swipe_two';
                        break;
                    case 'advanced_swipe':
                        iconId = '#swipe-advanced_swipe';
                        break;
                    case 'swipe_resistance':
                        iconId = '#swipe-swipe_resistance';
                        break;
                    default:
                        iconId = '#swipe-swipe_two'; // иконка по умолчанию
                }

                $use.setAttribute('xlink:href', iconId);
            }
        }


        // Установка текста ответов
        if (question.left_answer) {
            $tipLeft.appendChild(Object.assign(document.createElement("span"), {
                innerText: question.left_answer
            }));
            $answerLeft.innerText = question.left_answer;
        }
        if (question.right_answer) {
            $tipRight.appendChild(Object.assign(document.createElement("span"), {
                innerText: question.right_answer
            }));
            $answerRight.innerText = question.right_answer;
        }

        let hasSelected = false;

        // Инициализация Hammer.js для обработки свайпов
        const hammer = new Hammer($swipeContainer);
        hammer.get("swipe").set({
            direction: Hammer.DIRECTION_HORIZONTAL,
            velocity: 0.3,
            threshold: 10
        });

        /**
         * Применяет классы correct/select/fade для подсветки правильности выбора.
         *
         * @param {String} side — "left" или "right".
         */
        function applySelectionClasses(side) {
            const isLeft = side === "left";
            const selectedTip = isLeft ? $tipLeft : $tipRight;
            const otherTip = isLeft ? $tipRight : $tipLeft;
            const selectedAnswer = isLeft ? $answerLeft : $answerRight;
            const otherAnswer = isLeft ? $answerRight : $answerLeft;
            const isCorrect = !!(
                isLeft ? question.left_is_correct : question.right_is_correct
            );

            // Подсветка tip-элементов
            selectedTip.classList.add(
                isCorrect
                    ? `${classPrefix}__answers-option--correct`
                    : `${classPrefix}__answers-option--select`
            );
            otherTip.classList.add(
                isCorrect
                    ? `${classPrefix}__answers-option--fade`
                    : `${classPrefix}__answers-option--correct`
            );

            // Подсветка текстов ответов
            selectedAnswer.classList.add(
                isCorrect
                    ? `${classPrefix}__answers-option--correct`
                    : `${classPrefix}__answers-option--select`
            );
            otherAnswer.classList.add(
                isCorrect
                    ? `${classPrefix}__answers-option--fade`
                    : `${classPrefix}__answers-option--correct`
            );
        }

        /**
         * Общая логика после выбора (свайп или клик).
         *
         * @param {String} side — "left" или "right".
         */
        function finalizeSelection(side) {
            if (hasSelected) return;
            hasSelected = true;

            hammer.destroy(); // Отключаем дальнейшие свайпы

            applySelectionClasses(side);

            $answerText.style.display = "flex";
            $continueBtn.style.display = "flex";
            $retryBtn.style.display = "none";

            const answerKey = side === "left" ? "left_answer" : "right_answer";
            const answerObj = {text: question[answerKey]};

            onAnswer(question._uid, answerObj);
        }

        // Обработчики свайпов
        hammer.on("swipeleft", () => handleSwipe("left"));
        hammer.on("swiperight", () => handleSwipe("right"));

        /**
         * Анимирует карту и вызывает логику завершения выбора.
         *
         * @param {String} side — "left" или "right".
         */
        function handleSwipe(side) {
            const translateX = side === "left" ? "-100%" : "100%";
            const rotateDeg = side === "left" ? "-5deg" : "5deg";
            const tipHover = side === "left" ? $tipLeft : $tipRight;

            $swipeCard.style.transform = `translateX(${translateX}) rotate(${rotateDeg})`;
            tipHover.classList.add(`${classPrefix}__answers-option--hover`);

            setTimeout(() => {
                finalizeSelection(side);
                // Возвращаем карту в исходное положение
                $swipeCard.style.transform = "translateX(0)";
            }, 300);
        }

        // Обработчики кликов по текстовым блокам ответов (если пользователь предпочитает клик)
        $answerLeft.addEventListener("click", () => {
            if (!hasSelected && question.left_answer) {
                finalizeSelection("left");
            }
        });
        $answerRight.addEventListener("click", () => {
            if (!hasSelected && question.right_answer) {
                finalizeSelection("right");
            }
        });

        // Обработчики кнопок «Продолжить» и «Повторить»
        this._attachContinueRetry($tpl, onContinue, onRetry);

        // Если это последний вопрос, заменяем кнопку «Продолжить»
        // const $finalizeBtn = this._handleLastQuestion($tpl, showIndex, total, exerciseId, videoId);
        // if ($finalizeBtn) {
        //     $continueBtn = $finalizeBtn;
        // }

        container.appendChild($tpl);
    }

    // === Рендеринг open_text-вопросов ===

    /**
     * Рендерит вопрос типа «open_text» (открытый ввод).
     *
     * @param {Object} question  — объект вопроса с текстом question.text.
     * @param {Number} showIndex — порядковый номер вопроса.
     * @param {Function} onAnswer  — колбэк (questionUid, inputText).
     * @param {Function} onContinue — колбэк «Продолжить».
     * @param {Function} onRetry    — колбэк «Повторить».
     * @param {Object} opts — { container, template, classPrefix, dataPrefix, total }.
     */
    static _renderOpen(
        question,
        showIndex,
        onAnswer,
        onContinue,
        onRetry,
        opts
    ) {
        const {container, template, classPrefix, dataPrefix, total, exerciseId, videoId} = opts;
        const $tpl = document.importNode(template.content, true);

        // Заполняем общие поля
        this._fillCommonFields(question, showIndex, {total, classPrefix, dataPrefix}, $tpl);

        // Элементы из шаблона
        const $answerText = queryDataAttr("response-text", $tpl);
        let $continueBtn = queryDataAttr("continue-btn", $tpl);
        const $retryBtn = queryDataAttr("retry-btn", $tpl);
        const $textarea = queryDataAttr("question-textarea", $tpl);
        const $textareaBtn = queryDataAttr("textarea-done-btn", $tpl);

        let hasAnswered = false;

        $textareaBtn.addEventListener("click", async () => {
            if (hasAnswered) return;
            const inputValue = $textarea.value.trim();
            if (!inputValue) {
                // Можно добавить визуальное предупреждение: «Введите ответ»
                return;
            }
            hasAnswered = true;

            // Показать разбор и кнопку «Продолжить», скрыть «Повторить» и кнопку отправки
            $answerText.style.display = "flex";
            $continueBtn.style.display = "flex";
            $retryBtn.style.display = "none";
            $textareaBtn.style.display = "none";

            await onAnswer(question._uid, inputValue);
        });

        // Обработчики кнопок «Продолжить» и «Повторить»
        this._attachContinueRetry($tpl, onContinue, onRetry);

        // Если это последний вопрос, заменяем кнопку «Продолжить»
        // const $finalizeBtn = this._handleLastQuestion($tpl, showIndex, total, exerciseId, videoId);
        // if ($finalizeBtn) {
        //     $continueBtn = $finalizeBtn;
        // }

        container.appendChild($tpl);
    }
}
